// src/components/ProfileTab.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Badge, 
  ListGroup, 
  Button, 
  Accordion,
  Spinner,
  Alert,
  Form,
  InputGroup,
  Pagination,
  Modal
} from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { useTasks } from '../contexts/TasksContext';
import TaskForm from './TaskForm';
import TaskViewModal from './TaskViewModal';
import ProfileTasksBoard from './ProfileTasksBoard';

const ProfileTab = () => {
  const { user } = useAuth();
  const { 
    tasks,
    totalTasks,
    loading, 
    loadTasks, 
    createTask, 
    assignTask,
    completeTask,
    deleteTask
  } = useTasks();
  
  const [activeKey, setActiveKey] = useState('1');
  const isMounted = useRef(false); // для отслеживания первого рендера

  // Загрузка фильтров из localStorage
  const getSavedFilters = () => {
    try {
      const saved = localStorage.getItem('profile_task_filters');
      return saved ? JSON.parse(saved) : {
        status: '',
        priority: '',
        search: ''
      };
    } catch {
      return {
        status: '',
        priority: '',
        search: ''
      };
    }
  };

  const [taskFilters, setTaskFilters] = useState(getSavedFilters);
  const [currentPage, setCurrentPage] = useState(1);
  
  const getSavedPagination = () => {
    try {
      const saved = localStorage.getItem('profile_pagination');
      return saved ? JSON.parse(saved) : { tasksPerPage: 6 };
    } catch {
      return { tasksPerPage: 6 };
    }
  };

  const [tasksPerPage, setTasksPerPage] = useState(getSavedPagination().tasksPerPage);

  // Состояния для модальных окон
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [creatingTask, setCreatingTask] = useState(false);

  // Состояние для вкладок
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const saved = localStorage.getItem('profile_view_mode');
      return (saved === 'list' || saved === 'board') ? saved : 'list';
    } catch {
      return 'list';
    }
  });

  // Сохранение настроек в localStorage
  useEffect(() => {
    localStorage.setItem('profile_task_filters', JSON.stringify(taskFilters));
  }, [taskFilters]);

  useEffect(() => {
    localStorage.setItem('profile_pagination', JSON.stringify({ tasksPerPage }));
  }, [tasksPerPage]);

  useEffect(() => {
    localStorage.setItem('profile_current_page', currentPage.toString());
  }, [currentPage]);

  useEffect(() => {
    localStorage.setItem('profile_view_mode', activeTab);
  }, [activeTab]);

  // Восстановление сохранённой страницы и количества на странице при монтировании
  useEffect(() => {
    const savedPage = localStorage.getItem('profile_current_page');
    if (savedPage) {
      setCurrentPage(parseInt(savedPage, 10));
    }
    const savedPerPage = localStorage.getItem('profile_pagination');
    if (savedPerPage) {
      try {
        const { tasksPerPage: saved } = JSON.parse(savedPerPage);
        setTasksPerPage(saved);
      } catch (e) {}
    }
  }, []);

  // ========== ЗАГРУЗКА ЗАДАЧ ==========
  // Эффект для первичной загрузки при монтировании (только один раз)
  useEffect(() => {
    const skip = (currentPage - 1) * tasksPerPage;
    const limit = tasksPerPage;
    loadTasks({
      ...taskFilters,
      skip,
      limit,
      my_tasks: true, // обязательно!
    });
    isMounted.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // пустой массив – выполняется только при монтировании

  // Эффект для загрузки при изменении фильтров, страницы или количества на странице
  useEffect(() => {
    // Пропускаем первый вызов, чтобы не дублировать загрузку
    if (!isMounted.current) return;

    const skip = (currentPage - 1) * tasksPerPage;
    const limit = tasksPerPage;
    loadTasks({
      ...taskFilters,
      skip,
      limit,
      my_tasks: true,
    });
  }, [taskFilters, currentPage, tasksPerPage, loadTasks]);

  // Корректировка страницы, если она стала больше допустимой
  useEffect(() => {
    const totalPages = Math.ceil(totalTasks / tasksPerPage);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalTasks, tasksPerPage]);

  // ========== ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ ==========
  const getStatusBadgeVariant = (status) => {
    const map = {
      'open': 'secondary',
      'assigned': 'info',
      'in_progress': 'warning',
      'completed': 'success',
      'cancelled': 'danger',
      'reopened': 'primary'
    };
    return map[status] || 'secondary';
  };

  const getPriorityBadgeVariant = (priority) => {
    const map = {
      'low': 'success',
      'medium': 'info',
      'high': 'warning',
      'urgent': 'danger'
    };
    return map[priority] || 'secondary';
  };

  const getStatusText = (status) => {
    const map = {
      'open': 'Открыта',
      'assigned': 'Назначена',
      'in_progress': 'В работе',
      'completed': 'Выполнена',
      'cancelled': 'Отменена',
      'reopened': 'Переоткрыта'
    };
    return map[status] || status;
  };

  const getPriorityText = (priority) => {
    const map = {
      'low': 'Низкий',
      'medium': 'Средний',
      'high': 'Высокий',
      'urgent': 'Срочный'
    };
    return map[priority] || priority;
  };

  // Так как мы загружаем только свои задачи, tasks уже содержит только их
  const currentTasks = tasks;

  const handleFilterChange = (key, value) => {
    setTaskFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setTaskFilters({ status: '', priority: '', search: '' });
    setCurrentPage(1);
  };

  const resetAllSettings = () => {
    setTaskFilters({ status: '', priority: '', search: '' });
    setTasksPerPage(6);
    setCurrentPage(1);
  };

  const handleCreateTask = async (taskData) => {
    setCreatingTask(true);
    try {
      const result = await createTask(taskData);
      if (result.success) {
        setShowCreateModal(false);
        // После создания задачи перезагружаем список
        const skip = (currentPage - 1) * tasksPerPage;
        const limit = tasksPerPage;
        await loadTasks({ ...taskFilters, skip, limit, my_tasks: true });
      }
      return result;
    } catch (error) {
      console.error('Error creating task:', error);
      return { success: false, error: 'Не удалось создать задачу' };
    } finally {
      setCreatingTask(false);
    }
  };

  const handleOpenTask = (task) => {
    setSelectedTask(task);
    setShowTaskModal(true);
  };

  const handleCloseTaskModal = () => {
    setShowTaskModal(false);
    setSelectedTask(null);
  };

  const handleAssignTask = async (taskId, assigneeId) => {
    const result = await assignTask(taskId, assigneeId);
    if (result.success && selectedTask?.id === taskId) {
      setSelectedTask(result.task);
    }
    return result;
  };

  const handleCompleteTask = async (taskId, actualHours) => {
    const result = await completeTask(taskId, actualHours);
    if (result.success && selectedTask?.id === taskId) {
      setSelectedTask(result.task);
    }
    return result;
  };

  const handleDeleteTask = async (taskId) => {
    const result = await deleteTask(taskId);
    if (result.success) {
      if (selectedTask?.id === taskId) {
        setSelectedTask(null);
        setShowTaskModal(false);
      }
      // После удаления перезагружаем список
      const skip = (currentPage - 1) * tasksPerPage;
      const limit = tasksPerPage;
      await loadTasks({ ...taskFilters, skip, limit, my_tasks: true });
    }
    return result;
  };

  const getTaskStats = () => {
    if (!user?.id) return { total: 0, assigned: 0, created: 0, completed: 0, inProgress: 0, overdue: 0 };

    const stats = {
      total: totalTasks,
      assigned: tasks.filter(task => task.assignee_id === user.id).length,
      created: tasks.filter(task => task.creator_id === user.id).length,
      completed: tasks.filter(task => task.status === 'completed').length,
      inProgress: tasks.filter(task => task.status === 'in_progress').length,
      overdue: tasks.filter(task => {
        if (!task.due_date) return false;
        return new Date(task.due_date) < new Date() && !['completed', 'cancelled'].includes(task.status);
      }).length
    };
    return stats;
  };

  const taskStats = getTaskStats();
  const hasActiveFilters = Object.values(taskFilters).some(v => v !== '');
  const totalPages = Math.ceil(totalTasks / tasksPerPage);

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
        disabled={currentPage === 1}
      />
    );

    if (start > 1) {
      items.push(<Pagination.Item key={1} onClick={() => setCurrentPage(1)}>1</Pagination.Item>);
      if (start > 2) items.push(<Pagination.Ellipsis key="start-ellipsis" />);
    }

    for (let i = start; i <= end; i++) {
      items.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>
          {i}
        </Pagination.Item>
      );
    }

    if (end < totalPages) {
      if (end < totalPages - 1) items.push(<Pagination.Ellipsis key="end-ellipsis" />);
      items.push(
        <Pagination.Item key={totalPages} onClick={() => setCurrentPage(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
        disabled={currentPage === totalPages}
      />
    );

    return <Pagination className="mb-0">{items}</Pagination>;
  };

  if (!user) {
    return (
      <Row className="g-4">
        <Col>
          <Card className="text-center">
            <Card.Body>
              <div className="py-5">
                <i className="bi bi-exclamation-triangle display-1 text-warning"></i>
                <h4 className="mt-3">Данные пользователя не загружены</h4>
                <p className="text-muted">Пожалуйста, подождите...</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  }

  const hasTelegramData = user.telegram_id || user.telegram_username;
  const emailVerified = user.email_verified || user.email_verified_at;

  return (
    <Container fluid className="py-4">
      <Row className="g-4">
        {/* Левая колонка – профиль */}
        <Col lg={2}>
          <Card className="shadow-sm">
            <Card.Header className="bg-primary text-white position-relative">
              <h5 className="mb-0"><i className="bi bi-person-badge me-2"></i>Профиль</h5>
              {user.is_superuser && (
                <Badge bg="warning" className="position-absolute top-0 end-0 mt-2 me-2">
                  <i className="bi bi-star-fill me-1"></i> SUPERUSER
                </Badge>
              )}
            </Card.Header>
            <Card.Body className="text-center">
              <div className="mb-4">
                {user.telegram_photo_url ? (
                  <img 
                    src={user.telegram_photo_url} 
                    alt="Avatar"
                    className="rounded-circle mb-3 border"
                    style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                  />
                ) : (
                  <div 
                    className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3 text-white fw-bold"
                    style={{ width: '100px', height: '100px', fontSize: '2.5rem', background: 'linear-gradient(135deg, #8dc0fb 0%, #6ba0d9 100%)' }}
                  >
                    {(() => {
                      const fn = user?.telegram_first_name || '';
                      const ln = user?.telegram_last_name || '';
                      if (fn || ln) return `${fn.charAt(0)}${ln.charAt(0)}`.toUpperCase();
                      if (user?.name) return user.name.split(' ').map(w => w[0]).join('').toUpperCase().substring(0, 2);
                      return 'U';
                    })()}
                  </div>
                )}
                <h4 className="mb-1">
                  {(() => {
                    const fn = user?.telegram_first_name || '';
                    const ln = user?.telegram_last_name || '';
                    if (fn && ln) return `${fn} ${ln}`;
                    if (fn) return fn;
                    if (ln) return ln;
                    return user?.name || 'Пользователь';
                  })()}
                </h4>
                {user.telegram_username && (
                  <div className="mb-2">
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      className="rounded-pill"
                      onClick={() => window.open(`https://t.me/${user.telegram_username}`, '_blank')}
                    >
                      <i className="bi bi-telegram me-1"></i> @{user.telegram_username}
                    </Button>
                  </div>
                )}
                <Badge 
                  bg={user.role === 'admin' ? 'danger' : user.role === 'moderator' ? 'warning' : 'secondary'} 
                  className="fs-6 px-3 py-2" style={{ borderRadius: '20px' }}
                >
                  <i className="bi bi-shield-check me-1"></i>
                  {user.role === 'admin' ? 'Администратор' : user.role === 'moderator' ? 'Модератор' : 'Пользователь'}
                </Badge>
              </div>
              <div className="d-flex justify-content-center align-items-center mb-4">
                <div className="me-3">
                  <div className={`rounded-circle ${user.is_active ? 'bg-success' : 'bg-secondary'}`} style={{ width: '12px', height: '12px' }}></div>
                </div>
                <span className={user.is_active ? 'text-success fw-bold' : 'text-secondary'}>
                  {user.is_active ? 'Активен' : 'Неактивен'}
                </span>
              </div>
            </Card.Body>
          </Card>

          {/* Статистика задач */}
          <Card className="mt-4 shadow-sm">
            <Card.Header className="bg-info text-white">
              <h6 className="mb-0"><i className="bi bi-graph-up me-2"></i>Статистика задач</h6>
            </Card.Header>
            <Card.Body>
              <Row className="g-2 text-center">
                <Col xs={6}>
                  <div className="border rounded p-2 bg-light">
                    <div className="text-primary fs-5 fw-bold">{taskStats.total}</div>
                    <div className="text-muted small">Всего задач</div>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="border rounded p-2 bg-light">
                    <div className="text-success fs-5 fw-bold">{taskStats.completed}</div>
                    <div className="text-muted small">Выполнено</div>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="border rounded p-2 bg-light">
                    <div className="text-warning fs-5 fw-bold">{taskStats.inProgress}</div>
                    <div className="text-muted small">В работе</div>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="border rounded p-2 bg-light">
                    <div className="text-danger fs-5 fw-bold">{taskStats.overdue}</div>
                    <div className="text-muted small">Просрочено</div>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="border rounded p-2 bg-light">
                    <div className="text-info fs-5 fw-bold">{taskStats.assigned}</div>
                    <div className="text-muted small">Назначено</div>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="border rounded p-2 bg-light">
                    <div className="text-secondary fs-5 fw-bold">{taskStats.created}</div>
                    <div className="text-muted small">Создано</div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Детальная информация */}
          <Card className="mt-4 shadow-sm">
            <Accordion activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
              <Accordion.Item eventKey="0">
                <Accordion.Header>
                  <h6 className="mb-0"><i className="bi bi-info-circle me-2"></i>Детальная информация</h6>
                </Accordion.Header>
                <Accordion.Body className="p-0">
                  <ListGroup variant="flush">
                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">Полное имя:</span>
                      <Badge bg="light" text="dark" className="font-monospace">
                        {(() => {
                          const fn = user?.telegram_first_name || '';
                          const ln = user?.telegram_last_name || '';
                          if (fn && ln) return `${fn} ${ln}`;
                          if (fn) return fn;
                          if (ln) return ln;
                          return user?.name || 'Пользователь';
                        })()}
                      </Badge>
                    </ListGroup.Item>
                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">ID пользователя:</span>
                      <Badge bg="light" text="dark" className="font-monospace">#{user.id}</Badge>
                    </ListGroup.Item>
                    {user.telegram_id && (
                      <ListGroup.Item className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">Telegram ID:</span>
                        <Badge bg="light" text="dark" className="font-monospace">{user.telegram_id}</Badge>
                      </ListGroup.Item>
                    )}
                    {user.max_user_id && (
                      <ListGroup.Item className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">Max ID:</span>
                        <Badge bg="light" text="dark" className="font-monospace">
                          {user.max_user_id}
                        </Badge>
                      </ListGroup.Item>
                    )}
                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">Email:</span>
                      <div className="d-flex align-items-center">
                        <Badge bg="light" text="dark" className="font-monospace me-2">{user.email || 'Не указан'}</Badge>
                        {user.email && emailVerified && (
                          <Badge bg="success" className="fs-7"><i className="bi bi-check-lg me-1"></i>Подтвержден</Badge>
                        )}
                      </div>
                    </ListGroup.Item>
                    {user.phone && (
                      <ListGroup.Item className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">Телефон:</span>
                        <Badge bg="light" text="dark" className="font-monospace">
                          {user.phone}
                        </Badge>
                      </ListGroup.Item>
                    )}

                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">Статус аккаунта:</span>
                      <Badge bg={user.is_active ? 'success' : 'secondary'}>{user.is_active ? 'Активен' : 'Заблокирован'}</Badge>
                    </ListGroup.Item>
                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">Права:</span>
                      <div className="d-flex align-items-center">
                        <Badge bg={user.role === 'admin' ? 'danger' : user.role === 'moderator' ? 'warning' : 'secondary'} className="fs-7 me-2">
                          {user.role === 'admin' && <i className="bi bi-star-fill me-1"></i>}
                          {user.role === 'moderator' && <i className="bi bi-shield me-1"></i>}
                          {user.role === 'user' && <i className="bi bi-person me-1"></i>}
                          {user.role === 'admin' ? 'Администратор' : user.role === 'moderator' ? 'Модератор' : 'Пользователь'}
                        </Badge>
                        {user.is_superuser && (
                          <Badge bg="warning" className="fs-7"><i className="bi bi-star-fill me-1"></i>Суперпользователь</Badge>
                        )}
                      </div>
                    </ListGroup.Item>
                    {hasTelegramData && (
                      <ListGroup.Item>
                        <div className="d-flex align-items-center text-success mb-1">
                          <i className="bi bi-telegram me-2"></i>
                          <small className="fw-bold">Привязан Telegram</small>
                        </div>
                        <small className="text-muted">Аккаунт подключен через Telegram</small>
                      </ListGroup.Item>
                    )}
                  </ListGroup>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </Card>
        </Col>

        {/* Правая колонка – задачи пользователя */}
        <Col lg={10}>
          <Card className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0"><i className="bi bi-list-task me-2"></i>Мои задачи</h5>
              <div className="d-flex gap-2">
                <div className="btn-group">
                  <Button variant={activeTab === 'list' ? 'primary' : 'outline-primary'} onClick={() => setActiveTab('list')} size="sm" title="Список">
                    <i className="bi bi-grid-3x3-gap"></i> Сетка
                  </Button>
                  <Button variant={activeTab === 'board' ? 'primary' : 'outline-primary'} onClick={() => setActiveTab('board')} size="sm" title="Доска">
                    <i className="bi bi-kanban"></i> Доска
                  </Button>
                </div>
                <Button variant="outline-warning" size="sm" onClick={resetAllSettings} title="Сбросить все настройки">
                  <i className="bi bi-arrow-clockwise"></i>
                </Button>
                <Button variant="outline-primary" size="sm" onClick={() => {
                  const skip = (currentPage - 1) * tasksPerPage;
                  const limit = tasksPerPage;
                  loadTasks({ ...taskFilters, skip, limit, my_tasks: true });
                }} disabled={loading} title="Обновить список">
                  <i className="bi bi-arrow-clockwise"></i>
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {activeTab === 'list' ? (
                <>
                  {/* Фильтры задач */}
                  <Card className="mb-4">
                    <Card.Body className="py-3">
                      <Row className="g-3 align-items-end">
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>Поиск по названию</Form.Label>
                            <InputGroup>
                              <Form.Control
                                type="text"
                                placeholder="Поиск задач..."
                                value={taskFilters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                              />
                              {taskFilters.search && (
                                <Button variant="outline-secondary" onClick={() => handleFilterChange('search', '')}>
                                  <i className="bi bi-x"></i>
                                </Button>
                              )}
                            </InputGroup>
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Статус</Form.Label>
                            <Form.Select
                              value={taskFilters.status}
                              onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                              <option value="">Все статусы</option>
                              <option value="open">Открыта</option>
                              <option value="assigned">Назначена</option>
                              <option value="in_progress">В работе</option>
                              <option value="completed">Выполнена</option>
                              <option value="cancelled">Отменена</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Приоритет</Form.Label>
                            <Form.Select
                              value={taskFilters.priority}
                              onChange={(e) => handleFilterChange('priority', e.target.value)}
                            >
                              <option value="">Все приоритеты</option>
                              <option value="low">Низкий</option>
                              <option value="medium">Средний</option>
                              <option value="high">Высокий</option>
                              <option value="urgent">Срочный</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={2}>
                          <Form.Group>
                            <Form.Label>На странице</Form.Label>
                            <Form.Select
                              value={tasksPerPage}
                              onChange={(e) => setTasksPerPage(Number(e.target.value))}
                            >
                              <option value={3}>3</option>
                              <option value={6}>6</option>
                              <option value={9}>9</option>
                              <option value={12}>12</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>
                      {hasActiveFilters && (
                        <Row className="mt-3">
                          <Col>
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="d-flex flex-wrap gap-2">
                                {taskFilters.search && (
                                  <Badge bg="primary" className="d-flex align-items-center">
                                    Поиск: "{taskFilters.search}"
                                    <button className="btn-close btn-close-white ms-1" style={{ fontSize: '0.6rem' }} onClick={() => handleFilterChange('search', '')}></button>
                                  </Badge>
                                )}
                                {taskFilters.status && (
                                  <Badge bg="info" className="d-flex align-items-center">
                                    Статус: {taskFilters.status}
                                    <button className="btn-close btn-close-white ms-1" style={{ fontSize: '0.6rem' }} onClick={() => handleFilterChange('status', '')}></button>
                                  </Badge>
                                )}
                                {taskFilters.priority && (
                                  <Badge bg="warning" text="dark" className="d-flex align-items-center">
                                    Приоритет: {taskFilters.priority}
                                    <button className="btn-close ms-1" style={{ fontSize: '0.6rem' }} onClick={() => handleFilterChange('priority', '')}></button>
                                  </Badge>
                                )}
                              </div>
                              <Button variant="outline-secondary" size="sm" onClick={clearFilters}>
                                <i className="bi bi-x-lg me-1"></i> Очистить фильтры
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      )}
                    </Card.Body>
                  </Card>

                  {/* Индикатор загрузки */}
                  {loading && (
                    <div className="text-center py-4">
                      <Spinner animation="border" variant="primary" />
                      <div className="mt-2 text-muted">Загрузка задач...</div>
                    </div>
                  )}

                  {/* Список задач */}
                  {!loading && currentTasks.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="bi bi-inbox display-1 mb-3 opacity-50"></i>
                      <h5>Задачи не найдены</h5>
                      <p className="mb-4">
                        {hasActiveFilters ? 'Попробуйте изменить фильтры' : 'У вас пока нет задач'}
                      </p>
                      {!hasActiveFilters && (
                        <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                          <i className="bi bi-plus-lg me-1"></i> Создать первую задачу
                        </Button>
                      )}
                    </div>
                  ) : (
                    <>
                      <Row className="g-3">
                        {currentTasks.map(task => (
                          <Col key={task.id} md={6} lg={4}>
                            <Card className="h-100 task-card-sm">
                              <Card.Header className="d-flex justify-content-between align-items-start py-2">
                                <div className="flex-grow-1">
                                  <div className="d-flex align-items-center mb-1">
                                    <Badge bg={getStatusBadgeVariant(task.status)} className="me-1" style={{ fontSize: '0.7rem' }}>
                                      {getStatusText(task.status)}
                                    </Badge>
                                    <Badge bg={getPriorityBadgeVariant(task.priority)} style={{ fontSize: '0.7rem' }}>
                                      {getPriorityText(task.priority)}
                                    </Badge>
                                  </div>
                                  <h6 className="mb-0 task-title" style={{ fontSize: '0.9rem' }} title={task.title}>
                                    {task.title.length > 50 ? `${task.title.substring(0, 50)}...` : task.title}
                                  </h6>
                                </div>
                              </Card.Header>
                              <Card.Body className="py-2">
                                {task.description && (
                                  <p className="text-muted small mb-2" style={{ fontSize: '0.8rem' }}>
                                    {task.description.length > 80 ? `${task.description.substring(0, 80)}...` : task.description}
                                  </p>
                                )}
                                <div className="task-meta">
                                  <div className="mb-1">
                                    <small className="text-muted">Исполнитель:</small>
                                    <div className="fw-semibold small">
                                      {task.assignee?.id === user.id ? 'Вы' : task.assignee?.name || 'Не назначен'}
                                    </div>
                                  </div>
                                  {task.due_date && (
                                    <div className="mb-1">
                                      <small className="text-muted">Срок:</small>
                                      <div className={`fw-semibold small ${new Date(task.due_date) < new Date() && !['completed', 'cancelled'].includes(task.status) ? 'text-danger' : ''}`}>
                                        {new Date(task.due_date).toLocaleDateString('ru-RU')}
                                      </div>
                                    </div>
                                  )}
                                  {task.estimated_hours && (
                                    <div className="mb-1">
                                      <small className="text-muted">Оценка:</small>
                                      <div className="fw-semibold small">{task.estimated_hours}ч</div>
                                    </div>
                                  )}
                                </div>
                              </Card.Body>
                              <Card.Footer className="bg-transparent py-2">
                                <div className="d-flex justify-content-between align-items-center">
                                  <small className="text-muted">#{task.id}</small>
                                  <Button variant="outline-primary" size="sm" onClick={() => handleOpenTask(task)} style={{ fontSize: '0.8rem' }}>
                                    <i className="bi bi-eye me-1"></i> Открыть
                                  </Button>
                                </div>
                              </Card.Footer>
                            </Card>
                          </Col>
                        ))}
                      </Row>

                      {totalPages > 1 && (
                        <div className="mt-4">
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="text-muted small">
                              Задачи <strong>{(currentPage - 1) * tasksPerPage + 1}-{Math.min(currentPage * tasksPerPage, totalTasks)}</strong> из <strong>{totalTasks}</strong>
                            </div>
                            {renderPagination()}
                            <div className="text-muted small">
                              Страница <strong>{currentPage}</strong> из <strong>{totalPages}</strong>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : (
                <ProfileTasksBoard 
                  tasks={currentTasks}
                  onView={handleOpenTask}
                  getStatusBadgeVariant={getStatusBadgeVariant}
                  getPriorityBadgeVariant={getPriorityBadgeVariant}
                  onTaskUpdate={() => {}} // не нужно
                />
              )}
            </Card.Body>
          </Card>

          {/* Быстрые действия */}
          <Card className="mt-4 shadow-sm">
            <Card.Header>
              <h6 className="mb-0"><i className="bi bi-lightning me-2"></i>Быстрые действия</h6>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col md={6}>
                  <Button variant="primary" className="w-100 h-100 py-3" onClick={() => setShowCreateModal(true)}>
                    <i className="bi bi-plus-circle display-6 d-block mb-2"></i> Создать задачу
                  </Button>
                </Col>
                <Col md={6}>
                  <Button variant="outline-success" className="w-100 h-100 py-3" onClick={() => window.location.href = '/_admin/tasks'}>
                    <i className="bi bi-list-task display-6 d-block mb-2"></i> Все задачи
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Модальные окна */}
      <TaskViewModal
        task={selectedTask}
        onClose={handleCloseTaskModal}
        onEdit={(task) => { setShowTaskModal(false); console.log('Edit task:', task); }}
        onDelete={handleDeleteTask}
        onComplete={handleCompleteTask}
        onAssign={handleAssignTask}
        getStatusBadgeVariant={getStatusBadgeVariant}
        getPriorityBadgeVariant={getPriorityBadgeVariant}
      />

      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title><i className="bi bi-plus-circle me-2"></i>Создание задачи</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <TaskForm
            onSubmit={handleCreateTask}
            onCancel={() => setShowCreateModal(false)}
            loading={creatingTask}
          />
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default ProfileTab;